import { useState, useEffect } from "react";
import { apiClient, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Key,
  Bot,
  FileCode,
  Save,
  Loader2,
  CheckCircle2,
  ExternalLink,
  Eye,
  EyeOff,
  Link2,
  CloudOff
} from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [driveStatus, setDriveStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showOpenAiKey, setShowOpenAiKey] = useState(false);
  const [showGoogleSecret, setShowGoogleSecret] = useState(false);
  const [connectingDrive, setConnectingDrive] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, driveRes] = await Promise.all([
        apiClient.get("/settings"),
        apiClient.get("/drive/status").catch(() => ({ data: { connected: false } }))
      ]);
      setSettings(settingsRes.data);
      setDriveStatus(driveRes.data);
    } catch (error) {
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put("/settings", settings);
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const connectGoogleDrive = async () => {
    setConnectingDrive(true);
    try {
      const response = await apiClient.get("/drive/connect");
      window.location.href = response.data.authorization_url;
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes("Configura")) {
        toast.error("Primero configura GOOGLE_CLIENT_ID y GOOGLE_CLIENT_SECRET");
      } else {
        toast.error(detail || "Error al conectar con Google Drive");
      }
      setConnectingDrive(false);
    }
  };
  
  const disconnectGoogleDrive = async () => {
    if (!window.confirm("¿Desconectar Google Drive? Tu cuenta de login no se verá afectada.")) return;
    try {
      await apiClient.post("/drive/disconnect");
      toast.success("Google Drive desconectado");
      const driveRes = await apiClient.get("/drive/status").catch(() => ({ data: { connected: false } }));
      setDriveStatus(driveRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al desconectar");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner w-10 h-10" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight font-['Outfit']">
            Configuración
          </h1>
          <p className="text-slate-400 mt-1">
            Configura tus integraciones y preferencias
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className={`bg-cyan-600 hover:bg-cyan-500 text-white btn-glow ${user?.role !== "superadmin" ? "hidden" : ""}`}
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Guardar Cambios
        </Button>
      </div>

      <Tabs defaultValue={user?.role === "superadmin" ? "api" : "google"} className="space-y-6">
        <TabsList className="bg-slate-900/50 border border-white/5 p-1">
          {user?.role === "superadmin" && (
            <TabsTrigger 
              value="api" 
              className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white"
              data-testid="tab-api"
            >
              <Key className="w-4 h-4 mr-2" />
              API Keys
            </TabsTrigger>
          )}
          <TabsTrigger 
            value="google" 
            className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white"
            data-testid="tab-google"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Google
          </TabsTrigger>
          {user?.role === "superadmin" && (
            <TabsTrigger 
              value="ai" 
              className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white"
              data-testid="tab-ai"
            >
              <Bot className="w-4 h-4 mr-2" />
              IA
            </TabsTrigger>
          )}
        </TabsList>

        {/* API Keys Tab */}
        <TabsContent value="api" className="space-y-6">
          {/* Apify info card (read-only — managed globally by superadmin) */}
          <div className="glass rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Key className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">Apify (gestionada globalmente)</h3>
                <p className="text-sm text-slate-400 mt-1">
                  La API Key de Apify es la misma para todos los usuarios y la administra el equipo. Si necesitas cambiar la cuenta de Apify, contacta con el administrador.
                </p>
              </div>
            </div>
          </div>

          {/* Actor IDs */}
          <div className="glass rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Actor IDs de Apify</h3>
            <p className="text-sm text-slate-400 mb-6">
              IDs de los Actors de Apify para scraping. Puedes cambiarlos si encuentras mejores opciones.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Meta Ads Library Actor</Label>
                <Input
                  value={settings?.meta_actor_id || ""}
                  onChange={(e) => setSettings({ ...settings, meta_actor_id: e.target.value })}
                  placeholder="curious_coder/facebook-ads-library-scraper"
                  className="mt-1.5 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 font-mono text-sm"
                  data-testid="meta-actor-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">LinkedIn Ads Library Actor</Label>
                <Input
                  value={settings?.linkedin_actor_id || ""}
                  onChange={(e) => setSettings({ ...settings, linkedin_actor_id: e.target.value })}
                  placeholder="silva95gustavo/linkedin-ad-library-scraper"
                  className="mt-1.5 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 font-mono text-sm"
                  data-testid="linkedin-actor-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Google Ads Transparency Actor</Label>
                <Input
                  value={settings?.google_ads_actor_id || ""}
                  onChange={(e) => setSettings({ ...settings, google_ads_actor_id: e.target.value })}
                  placeholder="lexis-solutions/google-ads-scraper"
                  className="mt-1.5 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 font-mono text-sm"
                  data-testid="google-ads-actor-input"
                />
              </div>
            </div>
          </div>

          {/* Ads Limit block removed — now managed globally by superadmin from /admin → Configuración global */}
        </TabsContent>

        {/* Google Tab */}
        <TabsContent value="google" className="space-y-6">
          {/* Google Drive Connection Status */}
          <div className="glass rounded-xl p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                driveStatus.connected ? "bg-emerald-500/10" : "bg-slate-800"
              }`}>
                {driveStatus.connected ? (
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                ) : (
                  <CloudOff className="w-6 h-6 text-slate-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-white">Google Drive</h3>
                <p className="text-sm text-slate-400 mt-1">
                  {driveStatus.connected 
                    ? "Conectado · los informes y assets se guardarán en este Drive" 
                    : "No conectado · conecta para guardar informes y descargar creativos automáticamente"}
                </p>
                {driveStatus.connected && driveStatus.drive_email && (
                  <p className="text-xs text-slate-500 mt-2 font-mono truncate" data-testid="drive-account-email">
                    Cuenta de Drive: <span className="text-cyan-400">{driveStatus.drive_email}</span>
                  </p>
                )}
                <p className="text-xs text-slate-600 mt-2">
                  Puedes conectar un Google Drive con una cuenta diferente a la del login.
                </p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {!driveStatus.connected ? (
                  <Button
                    onClick={connectGoogleDrive}
                    disabled={connectingDrive}
                    className="bg-cyan-600 hover:bg-cyan-500 text-white"
                    data-testid="connect-drive-btn"
                  >
                    {connectingDrive ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4 mr-2" />
                    )}
                    Conectar Google Drive
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={connectGoogleDrive}
                      disabled={connectingDrive}
                      variant="outline"
                      className="border-white/10 text-slate-200 hover:bg-white/5"
                      data-testid="change-drive-btn"
                    >
                      {connectingDrive ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Link2 className="w-4 h-4 mr-2" />
                      )}
                      Cambiar cuenta
                    </Button>
                    <Button
                      onClick={disconnectGoogleDrive}
                      variant="outline"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                      data-testid="disconnect-drive-btn"
                    >
                      Desconectar
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Google OAuth Credentials — superadmin only (shared OAuth app) */}
          {user?.role === "superadmin" && (
          <div className="glass rounded-xl p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Google OAuth Credentials</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Necesario para login con Google y acceso a Drive/Docs
                </p>
                <a 
                  href="https://console.cloud.google.com/apis/credentials" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 mt-2"
                >
                  Crear credenciales en Google Cloud <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Google Client ID</Label>
                <Input
                  value={settings?.google_client_id || ""}
                  onChange={(e) => setSettings({ ...settings, google_client_id: e.target.value })}
                  placeholder="123456789.apps.googleusercontent.com"
                  className="mt-1.5 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 font-mono text-sm"
                  data-testid="google-client-id-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Google Client Secret</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showGoogleSecret ? "text" : "password"}
                    value={settings?.google_client_secret || ""}
                    onChange={(e) => setSettings({ ...settings, google_client_secret: e.target.value })}
                    placeholder="GOCSPX-..."
                    className="bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 font-mono text-sm pr-10"
                    data-testid="google-client-secret-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowGoogleSecret(!showGoogleSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showGoogleSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="p-4 bg-slate-950/50 rounded-lg border border-white/5">
                <p className="text-xs text-slate-400">
                  <strong className="text-slate-300">Redirect URIs autorizadas:</strong>
                </p>
                <code className="text-xs text-cyan-400 block mt-1 break-all">
                  {window.location.origin}/api/auth/google/callback
                </code>
              </div>
            </div>
          </div>
          )}
        </TabsContent>

        {/* AI Tab */}
        <TabsContent value="ai" className="space-y-6">
          <div className="glass rounded-xl p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Bot className="w-6 h-6 text-purple-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">Proveedor de IA</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Elige qué servicio de IA usar para el análisis de anuncios
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Emergent Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-950/50 rounded-lg border border-white/5">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className={`w-5 h-5 ${settings?.use_emergent_ai ? "text-emerald-400" : "text-slate-600"}`} />
                  <div>
                    <p className="font-medium text-white">Usar IA de Emergent (GPT-5.2)</p>
                    <p className="text-xs text-slate-500">Sin configuración adicional</p>
                  </div>
                </div>
                <Switch
                  checked={settings?.use_emergent_ai ?? true}
                  onCheckedChange={(checked) => setSettings({ ...settings, use_emergent_ai: checked })}
                  data-testid="use-emergent-switch"
                />
              </div>

              {/* OpenAI Key (only if not using Emergent) */}
              {!settings?.use_emergent_ai && (
                <div className="animate-fade-in">
                  <Label className="text-slate-300">OpenAI API Key</Label>
                  <div className="relative mt-1.5">
                    <Input
                      type={showOpenAiKey ? "text" : "password"}
                      value={settings?.openai_api_key || ""}
                      onChange={(e) => setSettings({ ...settings, openai_api_key: e.target.value })}
                      placeholder="sk-..."
                      className="bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 font-mono text-sm pr-10"
                      data-testid="openai-key-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowOpenAiKey(!showOpenAiKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showOpenAiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Obtén tu API key en <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">platform.openai.com</a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Prompts Tab removed — backend builds the analysis prompt directly with platform-aware structure */}
      </Tabs>
    </div>
  );
}
